import React, { useContext, useEffect, useMemo, useState } from "react";

import { LinearProgress } from "@mui/material";
import { Document, Page, pdfjs } from "react-pdf";
import { EntityType, RuleCategory, resizeBoundingBoxSurroundIntersectingBlocks } from "Backend";
import { StateHandle } from "../StateHandle";
import Flex from "./Flex";
import theme from "theme";
import { Block, BlockBoundingBox } from "generated/graphql";
import { AnnotationsContext } from "contexts/AnnotationsContext";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import { PageCallback } from "react-pdf/dist/cjs/shared/types.d.ts";
import { P } from "theme/typography";

pdfjs.GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/build/pdf.worker.min.js", import.meta.url).toString();

const options = {
  cMapUrl: "cmaps/",
  standardFontDataUrl: "standard_fonts/",
  rangeChunkSize: 10485760,
};

function isPageLandscape(rotate: number): boolean {
  return rotate === 90 || rotate === 270;
}

/**
 * Convert a size you want in pixels accounting for the screen resolution
 * @param value The size you want in pixels
 * @param dpr window.devicePixelRatio
 * @returns the value needed to match the size you want in pixels
 */
function toPx(value: number, dpr: number): number {
  return value / dpr;
}

const drawCheckMark = (body: {
  ctx: CanvasRenderingContext2D;
  x: number;
  y: number;
  width: number;
  blockHeight: number;
  dpr: number;
}) => {
  const { ctx, x, y, dpr, blockHeight } = body;
  const checkmarkWidth = toPx(body.width, dpr);
  const checkmarkHeight = checkmarkWidth * (2 / 3);
  const x1 = x;
  const x2 = x + checkmarkWidth / 4;
  const x3 = x + checkmarkWidth;
  const y1 = y + blockHeight / 2;
  const y2 = y1 + checkmarkHeight / 2;
  const y3 = y1 - checkmarkHeight / 2;

  ctx.globalAlpha = 0.8;
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.lineTo(x3, y3);
  ctx.lineWidth = 1;
  ctx.strokeStyle = theme.palette.success.main;
  ctx.stroke();
};

const styles = {
  page: {
    margin: "0 auto",
  },
  pageScroll: {
    borderRadius: 0,
  },
  placeholder: {
    borderRadius: 3,
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
};

const LoadingProgress = (props: { loadedPercent: number }) => {
  return (
    <Flex width="100%" height="100%" justifyContent="center" alignItems="center">
      <LinearProgress sx={{ width: 200 }} variant="determinate" color="success" value={props.loadedPercent} />
    </Flex>
  );
};

export interface Annotation {
  top: number;
  left: number;
  width: number;
  height: number;
  color: string;
  type?: EntityType | RuleCategory | "Add";
  label?: string;
  pinned?: boolean;
}

export interface PDFInfo {
  numPages: number;
  // width: number;
  // height: number;
}

interface OnPDFClick {
  x: number;
  y: number;
  pixelX: number;
  pixelY: number;
}

export interface SegNumPageNum {
  segmentNumber: number;
  pageNumber: number;
}

export interface PDFCanvasProps {
  pdfUrl: string;
  pdfSize: number;
  pageNumber: number;
  scale: number;
  annotations?: Annotation[];
  showAnnotations?: boolean;
  className?: string;
  userRotateState: StateHandle<number>;
  onLoad?: (info: PDFInfo) => void;
  onClick?: (coordinates: OnPDFClick) => void;
  onHover?: (x: number, y: number) => void;
  onDrag?: (x: number, y: number) => void;
  onDragStart?: (x: number, y: number) => void;
  onDragEnd?: (x: number, y: number) => void;
  setPageProxy: (page: PageCallback | undefined) => void;
  pageProxy: PageCallback | undefined;
  blocks: Block[];
  onPageChanged?: (segNumPageNum: SegNumPageNum) => void;
  segmentId: number;
}

const PDFCanvas = (props: PDFCanvasProps) => {
  const annContext = useContext(AnnotationsContext);
  const { scale, setPageProxy, pageProxy, onPageChanged } = props;
  const [mouseDown, setMouseDown] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);
  const rootRef = React.createRef<HTMLDivElement>();
  const [canvas, setCanvasRef] = React.useState<HTMLCanvasElement | undefined>(undefined);
  const [pageCanvasRef, setPageCanvasRef] = React.useState<HTMLCanvasElement | undefined>(undefined);
  const [loadedPercent, setLoadedPercent] = React.useState(0);
  const [userRotate] = props.userRotateState;
  const [pageRotate, setPageRotate] = React.useState(0);
  const [lastPageSeen, setLastPageSeen] = useState<SegNumPageNum | null>(null);
  const dpr = window.devicePixelRatio || 1;

  const canvasRefSet = React.useCallback((ref: HTMLCanvasElement) => {
    setCanvasRef(ref);
  }, []);

  const pageCanvasRefSet = React.useCallback((ref: HTMLCanvasElement) => setPageCanvasRef(ref), []);

  function drawDocumentAnnotation(
    ctx: CanvasRenderingContext2D,
    boundingBox: BlockBoundingBox,
    selected: boolean,
    exportIncluded: boolean
  ) {
    if (!canvas) return;
    const scaledHeight = canvas.height / dpr;
    const scaledWidth = canvas.width / dpr;
    const y = boundingBox.top * scaledHeight;
    const x = boundingBox.left * scaledWidth;
    const width = boundingBox.width * scaledWidth;
    const height = boundingBox.height * scaledHeight;
    // border width scales with zoom and dpr and scale, grows/shrinks in thickness depending on zoom and dpr
    const borderWidth = 2 / dpr;
    // if selected, color is yellor or primary main, otherwise black
    // to pick between yellow and primary main, use exportIncluded
    const fillColor = exportIncluded ? "black" : theme.palette.primary.main;
    const fillOpacity = 0.05;
    const strokeColor = exportIncluded ? "yellow" : theme.palette.primary.main;
    const strokeOpacity = exportIncluded ? 1 : 0.8;

    ctx.fillStyle = selected ? fillColor : "black";
    ctx.globalAlpha = selected ? fillOpacity : 0.05;
    ctx.fillRect(x, y, width, height);
    ctx.globalAlpha = selected ? strokeOpacity : 0.15;
    ctx.strokeStyle = selected ? strokeColor : "black";
    ctx.lineWidth = borderWidth;
    ctx.strokeRect(x + borderWidth / 2, y + borderWidth / 2, width - borderWidth, height - borderWidth);
  }

  function drawPin(ctx: CanvasRenderingContext2D, annotation: Annotation) {
    if (!canvas) return;
    const scaledHeight = canvas.height / dpr;
    const scaledWidth = canvas.width / dpr;
    ctx.globalAlpha = 0.4;
    const top = Math.round(annotation.top * scaledHeight);
    const left = Math.round(annotation.left * scaledWidth);
    const width = Math.round(annotation.width * scaledWidth);
    const height = Math.round(annotation.height * scaledHeight);
    ctx.fillStyle = annotation.color;
    ctx.fillRect(left, top, width, height);
    if (annotation.label) {
      ctx.font = "6px sans-serif";
      ctx.globalAlpha = 0.7;
      ctx.fillStyle = "black";
      ctx.fillText(annotation.label, left + width, top);
      ctx.globalAlpha = 0.4;
    }

    if (annotation.pinned) {
      drawCheckMark({
        ctx,
        x: left + width + toPx(4, dpr),
        y: top,
        width: 15,
        blockHeight: height,
        dpr,
      });
    }
  }

  // Sizing effect
  useEffect(() => {
    if (!canvas || !pageProxy || !pageCanvasRef) return;
    const landscape = isPageLandscape(userRotate);
    const pageWidth = (landscape ? pageCanvasRef.height : pageCanvasRef.width) / dpr;
    const pageHeight = (landscape ? pageCanvasRef.width : pageCanvasRef.height) / dpr;

    canvas.width = pageWidth;
    canvas.height = pageHeight;
    canvas.style.width = pageWidth + "px";
    canvas.style.height = pageHeight + "px";

    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.scale(dpr, dpr);
  }, [canvas, pageProxy, scale, dpr, pageCanvasRef, userRotate]);

  const onLoadProgress = (loaded: number, total: number) => {
    const perc = (loaded / total) * 100;
    setLoadedPercent(perc);
  };

  // Drawing effect
  useEffect(() => {
    if (!canvas || !pageProxy || !pageCanvasRef) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);

    if (annContext.drawing.enabled && !annContext.drawing.boundingBox && annContext.selectedDocumentAnnotation) {
      drawDocumentAnnotation(
        ctx,
        annContext.selectedDocumentAnnotation.boundingBox,
        true,
        annContext.selectedDocumentAnnotation.exportInclude
      );
    }

    if (annContext.drawing.boundingBox && annContext.drawing.enabled) {
      drawDocumentAnnotation(ctx, annContext.drawing.boundingBox, true, false);
    }

    if (props.annotations) {
      props.annotations.forEach((ann) => {
        drawPin(ctx, ann);
      });
    }
    if (annContext.drawing.enabled) {
      annContext.documentAnnotations?.forEach((ann) => {
        if (ann.id !== annContext.selectedDocumentAnnotation?.id) {
          drawDocumentAnnotation(ctx, ann.boundingBox, false, ann.exportInclude);
        }
      });
    }

    if (annContext.drawing.enabled) {
      return;
    }

    if (props.showAnnotations) {
      annContext.documentAnnotations?.forEach((ann) => {
        drawDocumentAnnotation(
          ctx,
          ann.boundingBox,
          annContext.selectedDocumentAnnotation?.id === ann.id || !annContext.selectedDocumentAnnotation,
          ann.exportInclude
        );
      });
    }
  }, [
    canvas,
    pageProxy,
    props.annotations,
    scale,
    dpr,
    pageCanvasRef,
    props.showAnnotations,
    annContext.documentAnnotations,
  ]);

  const translateMouseEventCoords = (evt: MouseEvent): [number, number] | undefined => {
    if (!canvas) return undefined;
    const rect = canvas.getBoundingClientRect();
    const x = (evt.clientX - rect.left) / rect.width;
    const y = (evt.clientY - rect.top) / rect.height;
    switch (userRotate) {
      case 0:
        return [x, y];
      case 90:
        return [y, 1 - x];
      case 180:
        return [1 - x, 1 - y];
      case 270:
        return [1 - y, x];
      default:
        return [x, y];
    }
  };

  function handleHover(e: React.MouseEvent<HTMLCanvasElement, MouseEvent>) {
    if (annContext.drawing.enabled) {
      document.body.style.cursor = "crosshair";

      if (props.onHover) props.onHover(0, 0);
      const mouseDown = e.buttons === 1;
      if (mouseDown) {
        if (!canvas) return;

        const ctx = canvas.getContext("2d");
        const coords = translateMouseEventCoords(e.nativeEvent);
        if (!ctx || !coords) return;
        const [x, y] = coords;
        const width = x - startX;
        const height = y - startY;

        const boundingBox = {
          top: Math.min(startY, y),
          left: Math.min(startX, x),
          width: Math.abs(width),
          height: Math.abs(height),
        };

        ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
        if (props.annotations) {
          if (!canvas) return;
          const ctx = canvas.getContext("2d");
          if (!ctx) return;
          props.annotations.forEach((ann) => {
            drawPin(ctx, ann);
          });
        }
        drawDocumentAnnotation(ctx, boundingBox, true, false);

        // draw all user annotations, not including the selected one
        annContext.documentAnnotations?.forEach((userAnn) => {
          if (userAnn.id !== annContext.selectedDocumentAnnotation?.id) {
            const ann = userAnn;
            drawDocumentAnnotation(ctx, ann.boundingBox, false, ann.exportInclude);
          }
        });
      }
    } else {
      const coords = translateMouseEventCoords(e.nativeEvent);
      if (coords) {
        const [x, y] = coords;
        if (mouseDown && props.onDrag) {
          props.onDrag(e.clientX, e.clientY);
        } else if (props.onHover) {
          props.onHover(x, y);
        }
      }
    }
  }

  function handleMouseDown(e: React.MouseEvent<HTMLCanvasElement, MouseEvent>) {
    if (annContext.drawing.enabled) {
      const coords = translateMouseEventCoords(e.nativeEvent);
      if (!canvas || !coords) return;
      const [x, y] = coords;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      setStartX(x);
      setStartY(y);
    } else {
      setMouseDown(true);
      if (props.onDragStart) props.onDragStart(e.clientX, e.clientY);
    }
  }

  function handleMouseUp(e: React.MouseEvent<HTMLCanvasElement, MouseEvent>) {
    setMouseDown(false);
    if (annContext.drawing.enabled) {
      if (!canvas) return;
      const coords = translateMouseEventCoords(e.nativeEvent);
      const ctx = canvas.getContext("2d");
      if (!coords || !ctx) return;
      const [x, y] = coords;
      const width = x - startX;
      const height = y - startY;

      const rect = {
        top: Math.min(startY, y),
        left: Math.min(startX, x),
        width: Math.abs(width),
        height: Math.abs(height),
      };

      const resized =
        annContext.drawing.mode === "snap" ? resizeBoundingBoxSurroundIntersectingBlocks(rect, props.blocks) : rect;
      annContext.drawing.setBoundingBox(resized);
      if (!annContext.annotationEditorOpen) {
        annContext.setPopoverOpen(true);
      }
    } else {
      if (props.onDragEnd) props.onDragEnd(e.clientX, e.clientY);
    }
  }

  const handleMouseEnter = () => {
    if (!annContext.drawing.enabled) return;
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLCanvasElement>) => {
    document.body.style.cursor = "default";
    setMouseDown(false);
    if (!canvas) return;
    if (e.buttons === 1 && annContext.drawing.enabled) {
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
      if (props.annotations) {
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        props.annotations.forEach((ann) => {
          drawPin(ctx, ann);
        });
      }
      annContext.documentAnnotations?.forEach((ann) => {
        if (ann.id !== annContext.selectedDocumentAnnotation?.id) {
          drawDocumentAnnotation(ctx, ann.boundingBox, false, ann.exportInclude);
        }
      });
    }
  };

  const Placeholder = (props: { msg?: string }) => (
    <div style={styles.placeholder}>
      <P variant="h6">
        <b>{props.msg}</b>
      </P>
    </div>
  );

  const pdfInput = React.useMemo(
    () => ({
      url: props.pdfUrl,
      withCredentials: true,
    }),
    [props.pdfUrl]
  );

  const calcTop = useMemo(() => {
    switch (userRotate) {
      case 0:
        return 0;
      case 90:
        return 0;
      case 180:
        return pageCanvasRef ? pageCanvasRef.height / dpr : 0;
      case 270:
        return pageCanvasRef ? pageCanvasRef.height / dpr : 0;
      default:
        return 0;
    }
  }, [userRotate, pageCanvasRef?.width, pageCanvasRef?.height, dpr]);
  const calcLeft = useMemo(() => {
    switch (userRotate) {
      case 0:
        return 0;
      case 90:
        return pageCanvasRef ? pageCanvasRef.width / dpr : 0;
      case 180:
        return pageCanvasRef ? pageCanvasRef.width / dpr : 0;
      case 270:
        return 0;
      default:
        return 0;
    }
  }, [userRotate, pageCanvasRef?.width, pageCanvasRef?.height, dpr]);

  return (
    <Document
      key={props.pdfUrl}
      className="docRoot"
      inputRef={rootRef}
      file={pdfInput}
      options={options}
      loading={<LoadingProgress loadedPercent={loadedPercent} />}
      error={<Placeholder msg="Error" />}
      noData={<Placeholder msg="Loading" />}
      onLoadSuccess={(pdfProxy) => {
        if (props.onLoad) {
          props.onLoad({ numPages: pdfProxy.numPages });
        }
      }}
      {...{
        onLoadProgress: (progress: any) => {
          onLoadProgress(progress.loaded, progress.total);
        },
      }}>
      <Page
        scale={scale}
        canvasRef={pageCanvasRefSet}
        rotate={(pageRotate + userRotate) % 360}
        renderTextLayer={false}
        renderAnnotationLayer={false}
        onLoadSuccess={(pageProxy: PageCallback) => {
          const rotate = (pageProxy as PageCallback & { rotate: number }).rotate;
          setPageRotate(rotate);
          setPageProxy(pageProxy);
        }}
        onRenderSuccess={(pageProxy) => {
          const segNumPageNum = {
            segmentNumber: props.segmentId,
            pageNumber: pageProxy.pageNumber,
          };
          if (
            lastPageSeen === null ||
            lastPageSeen.segmentNumber !== segNumPageNum.segmentNumber ||
            lastPageSeen.pageNumber !== segNumPageNum.pageNumber
          ) {
            if (onPageChanged) {
              onPageChanged(segNumPageNum);
            }
            setLastPageSeen(segNumPageNum);
          }
        }}
        loading={<LoadingProgress loadedPercent={loadedPercent} />}
        pageNumber={props.pageNumber}>
        {pageCanvasRef && (
          <canvas
            onMouseMove={handleHover}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            onMouseEnter={handleMouseEnter}
            style={{
              position: "absolute",
              transform: `rotate(${userRotate}deg)`,
              transformOrigin: "top left",
              top: calcTop,
              left: calcLeft,
            }}
            ref={canvasRefSet}
          />
        )}
      </Page>
    </Document>
  );
};

export default PDFCanvas;
